import { useCallback, useEffect, useState } from 'react';
import { Check, Copy } from 'lucide-react';
import { api, ApiError } from '../../../api.ts';
import { formatEuros } from '../../../format.ts';
import { fmtDay, type Delegation, type UrssafPeriod } from './types.ts';

type Frequency = 'MONTHLY' | 'QUARTERLY';
interface Data { frequency: Frequency; periods: UrssafPeriod[]; delegation: Delegation }

const STATUS: Record<UrssafPeriod['status'], { label: string; badge: string }> = {
  OPEN: { label: 'En cours', badge: 'badge-neutral' },
  TO_DECLARE: { label: 'À déclarer', badge: 'badge-pending' },
  OVERDUE: { label: 'En retard', badge: 'badge-unavailable' },
  DECLARED: { label: 'Déclarée', badge: 'badge-available' },
};

const ERRORS: Record<string, string> = {
  PERIOD_NOT_CLOSED: 'Cette période n’est pas terminée.',
  ALREADY_DECLARED: 'Cette période est déjà déclarée.',
  AMOUNT_CHANGED: 'Le montant a changé depuis votre validation : rechargez la page et vérifiez le nouveau montant.',
  DELEGATION_REQUIRED: 'Activez d’abord la délégation de déclaration.',
  URSSAF_NOT_ELIGIBLE: 'L’Urssaf indique que votre compte ne peut pas être déclaré par un tiers. Vérifiez votre compte sur autoentrepreneur.urssaf.fr.',
  BILLING_INCOMPLETE: 'Renseignez d’abord votre SIRET dans l’onglet « Facturation ».',
  URSSAF_UNAVAILABLE: 'Le service de l’Urssaf ne répond pas pour le moment. Réessayez dans quelques minutes, ou déclarez directement sur autoentrepreneur.urssaf.fr.',
  URSSAF_REJECTED: 'L’Urssaf a refusé la déclaration.',
  URSSAF_DISABLED: 'La déclaration déléguée n’est pas disponible pour le moment.',
  SUBMISSION_IN_PROGRESS: 'Un envoi est déjà en cours pour cette période.',
};

function describe(err: unknown, fallback: string) {
  if (!(err instanceof ApiError)) return fallback;
  return ERRORS[err.code] ?? fallback;
}

function DelegationCard({ delegation, onChange, setError }: { delegation: Delegation; onChange: (d: Data) => void; setError: (e: string | null) => void }) {
  const [agree, setAgree] = useState(false);
  const [busy, setBusy] = useState(false);
  const active = delegation.status === 'ACTIVE';

  async function enable() {
    setBusy(true);
    setError(null);
    try {
      onChange(await api.post<Data>('/extra/urssaf/delegation', { accept: true }));
      setAgree(false);
    } catch (err) {
      setError(describe(err, 'Activation impossible.'));
    } finally {
      setBusy(false);
    }
  }

  async function disable() {
    if (!window.confirm('Mettre fin à la délégation ? Vous déclarerez de nouveau vous-même sur autoentrepreneur.urssaf.fr.')) return;
    setBusy(true);
    setError(null);
    try {
      const res = await api.delete<Data & { urssafNotified: boolean }>('/extra/urssaf/delegation');
      onChange(res);
      if (!res.urssafNotified) setError('La délégation est retirée chez Mon Extra, mais l’Urssaf n’a pas pu être prévenue : retirez aussi le tiers déclarant depuis votre compte autoentrepreneur.urssaf.fr.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
        <div style={{ fontWeight: 800 }}>Déclarer en un clic via Mon Extra</div>
        <span className={`badge ${active ? 'badge-available' : 'badge-neutral'}`}>{active ? 'Activé' : 'Désactivé'}</span>
      </div>
      {active ? (
        <>
          <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
            Mon Extra est votre tiers déclarant{delegation.activatedAt ? ` depuis le ${fmtDay(delegation.activatedAt)}` : ''}. Vous validez vous-même chaque déclaration,
            période par période, après avoir vu le montant.
          </div>
          <button className="btn btn-secondary btn-sm" style={{ alignSelf: 'flex-start' }} onClick={disable} disabled={busy}>Mettre fin à la délégation</button>
        </>
      ) : (
        <>
          <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', lineHeight: 1.6 }}>{delegation.text}</div>
          <label style={{ display: 'flex', gap: 8, fontSize: 13, fontWeight: 600, alignItems: 'flex-start' }}>
            <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} style={{ marginTop: 3 }} />
            Je désigne Mon Extra comme tiers déclarant.
          </label>
          <button className="btn btn-primary btn-sm" style={{ alignSelf: 'flex-start' }} disabled={!agree || busy} onClick={enable}>Activer la délégation</button>
        </>
      )}
    </div>
  );
}

function SubmitPanel({ period, onDone, setError }: { period: UrssafPeriod; onDone: (d: Data) => void; setError: (e: string | null) => void }) {
  const [estimate, setEstimate] = useState<{ amountCents: number; contributionCents: number | null } | null>(null);
  const [busy, setBusy] = useState(false);

  async function preview() {
    setBusy(true);
    setError(null);
    try {
      setEstimate(await api.get(`/extra/urssaf/${period.key}/estimate`));
    } catch (err) {
      setError(describe(err, 'Estimation impossible.'));
    } finally {
      setBusy(false);
    }
  }

  async function submit() {
    if (!estimate) return;
    setBusy(true);
    setError(null);
    try {
      onDone(await api.post<Data>(`/extra/urssaf/${period.key}/submit`, { confirmAmountCents: estimate.amountCents }));
      setEstimate(null);
    } catch (err) {
      setError(describe(err, 'Transmission impossible.'));
    } finally {
      setBusy(false);
    }
  }

  if (!estimate) {
    return <button className="btn btn-primary btn-sm" onClick={preview} disabled={busy}>Déclarer via Mon Extra</button>;
  }
  return (
    <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 8, borderTop: '1px solid var(--border)', paddingTop: 10 }}>
      <div style={{ fontSize: 13, lineHeight: 1.6 }}>
        Vous allez déclarer <strong>{formatEuros(estimate.amountCents)}</strong> de chiffre d'affaires pour {period.label}.
        {estimate.contributionCents !== null && <> Cotisations estimées par l'Urssaf : <strong>{formatEuros(estimate.contributionCents)}</strong>.</>}
      </div>
      <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>La déclaration est transmise à l'Urssaf et ne peut plus être modifiée ici. Le paiement des cotisations se fait toujours sur votre compte Urssaf.</div>
      <div style={{ display: 'flex', gap: 8 }}>
        <button className="btn btn-primary btn-sm" onClick={submit} disabled={busy}>Confirmer et transmettre</button>
        <button className="btn btn-secondary btn-sm" onClick={() => setEstimate(null)} disabled={busy}>Annuler</button>
      </div>
    </div>
  );
}

export function Urssaf() {
  const [data, setData] = useState<Data | null>(null);
  const [copied, setCopied] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    api.get<Data>('/extra/urssaf').then(setData);
  }, []);
  useEffect(load, [load]);

  async function changeFrequency(frequency: Frequency) {
    if (!window.confirm('À ne modifier que si vous avez changé d’option auprès de l’URSSAF. Continuer ?')) return;
    setData(await api.put('/extra/urssaf/frequency', { frequency }));
  }

  async function markDeclared(p: UrssafPeriod) {
    setError(null);
    try {
      setData(await api.post(`/extra/urssaf/${p.key}/declared`));
    } catch (err) {
      setError(describe(err, 'Enregistrement impossible.'));
    }
  }

  function copy(p: UrssafPeriod) {
    navigator.clipboard.writeText((p.declarableCents / 100).toFixed(2).replace('.', ',')).then(() => {
      setCopied(p.key);
      setTimeout(() => setCopied(null), 2000);
    });
  }

  if (!data) return <div className="empty-state">Chargement…</div>;
  const delegated = data.delegation.available && data.delegation.status === 'ACTIVE';

  return (
    <>
      <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
        Le chiffre d'affaires à déclarer est celui encaissé pendant la période, mission Mon Extra et hors app confondues, net des avoirs.
        {delegated ? ' ' : <> La déclaration se fait sur <a href="https://autoentrepreneur.urssaf.fr" target="_blank" rel="noreferrer" style={{ fontWeight: 700, color: 'var(--accent-dark)' }}>autoentrepreneur.urssaf.fr</a> : copiez le montant, puis revenez marquer la période comme déclarée. </>}
        Une période sans vente se déclare quand même, à 0 €.
      </div>
      {error && <div className="error-banner">{error}</div>}

      {data.delegation.available && <DelegationCard delegation={data.delegation} onChange={setData} setError={setError} />}

      <div className="card" style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <label htmlFor="frequency" style={{ fontWeight: 700, fontSize: 13 }}>Périodicité choisie auprès de l'URSSAF</label>
        <select id="frequency" className="input" style={{ width: 'auto' }} value={data.frequency} onChange={(e) => changeFrequency(e.target.value as Frequency)}>
          <option value="MONTHLY">Mensuelle</option>
          <option value="QUARTERLY">Trimestrielle</option>
        </select>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {data.periods.map((p) => (
          <div key={p.key} className="card" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
              <div>
                <div style={{ fontWeight: 800, textTransform: 'capitalize' }}>{p.label}</div>
                <div style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>À déclarer avant le {fmtDay(p.deadline)}</div>
              </div>
              <span className={`badge ${STATUS[p.status].badge}`}>{STATUS[p.status].label}</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
              <div style={{ fontSize: 24, fontWeight: 800 }}>{formatEuros(p.declarableCents)}</div>
              <div style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>
                dont {formatEuros(p.inAppCents)} via Mon Extra et {formatEuros(p.outOfAppCents)} hors app · {p.entries} mouvement(s)
              </div>
            </div>
            {p.totalCents < 0 && (
              <div style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>Les remboursements ({formatEuros(p.totalCents)}) dépassent les ventes de la période : elle se déclare à 0 €.</div>
            )}
            {p.complementCents > 0 && (
              <div className="badge badge-pending" style={{ padding: '10px 14px', fontSize: 12.5, lineHeight: 1.5 }}>
                {formatEuros(p.complementCents)} ont été ajoutés depuis votre déclaration ({formatEuros(p.declaredAmountCents ?? 0)}) : à régulariser.
              </div>
            )}
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button className="btn btn-secondary btn-sm" onClick={() => copy(p)}>
                {copied === p.key ? <Check size={14} /> : <Copy size={14} />} {copied === p.key ? 'Copié' : 'Copier le montant'}
              </button>
              <a className="btn btn-secondary btn-sm" style={{ display: 'inline-flex' }} href={`/api/extra/revenues/export.csv?period=${p.key}`}>Détail CSV</a>
              {delegated && (p.status === 'TO_DECLARE' || p.status === 'OVERDUE') && <SubmitPanel period={p} onDone={setData} setError={setError} />}
              {!delegated && p.status !== 'OPEN' && (p.status !== 'DECLARED' || p.complementCents > 0) && (
                <button className="btn btn-primary btn-sm" onClick={() => markDeclared(p)}>
                  {p.status === 'DECLARED' ? 'Marquer le complément déclaré' : 'Marquer comme déclarée'}
                </button>
              )}
              {delegated && p.status === 'DECLARED' && p.complementCents > 0 && (
                <button className="btn btn-secondary btn-sm" onClick={() => markDeclared(p)}>Marquer le complément déclaré</button>
              )}
            </div>
            {p.declaredAt && p.complementCents === 0 && (
              <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>
                {p.submittedViaApp ? 'Transmise à l’Urssaf via Mon Extra' : 'Déclarée'} le {fmtDay(p.declaredAt)}.
                {p.submittedViaApp && p.contributionCents != null && ` Cotisations estimées : ${formatEuros(p.contributionCents)}.`}
              </div>
            )}
          </div>
        ))}
      </div>
    </>
  );
}
